import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { csInquiryApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 問い合わせダッシュボードデータを取得するフック。
 * @graph-connects csInquiryApi.getDashboard [calls] API クライアント
 */
export function useCsDashboard(from?: string, to?: string) {
  return useQuery({
    queryKey: queryKeys.csInquiry.dashboard(from, to),
    queryFn: () => csInquiryApi.getDashboard({ from, to }),
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット一覧を取得するフック。
 * @graph-connects csInquiryApi.getChats [calls] API クライアント
 */
export function useCsChats(params?: {
  from?: string;
  to?: string;
  state?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: queryKeys.csInquiry.chats(
      params?.from,
      params?.to,
      params?.state,
      params?.limit,
      params?.offset,
    ),
    queryFn: () => csInquiryApi.getChats(params),
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 特定チャットの詳細（メタ＋メッセージ一覧）を取得するフック。
 * @graph-connects csInquiryApi.getChatDetail [calls] API クライアント
 */
export function useCsChatDetail(userChatId: string | null) {
  const resolvedId = userChatId ?? '';
  return useQuery({
    queryKey: queryKeys.csInquiry.chatDetail(resolvedId),
    queryFn: () => csInquiryApi.getChatDetail(resolvedId),
    enabled: userChatId !== null,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 状態を取り込めていないチャットの総数を取得するフック。問い合わせ分析の3画面の帯に使う。3画面で同じキーを共有し、30分は取り直さない。
 * @graph-connects csInquiryApi.getImportStatus [calls] API クライアント
 */
export function useCsImportStatus() {
  return useQuery({
    queryKey: queryKeys.csInquiry.importStatus,
    queryFn: () => csInquiryApi.getImportStatus(),
    // 件数の元（チャットのメタ）は高々毎時しか変わらない。画面を行き来するたびに数え直さない。
    staleTime: 30 * 60_000,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 最新の保存済み AI 分析結果を取得する。ページ表示時に Firestore から即時読み込み。
 * @graph-connects csInquiryApi.getAiAnalysisLatest [calls] API クライアント
 */
export function useCsAiAnalysisLatest() {
  return useQuery({
    queryKey: queryKeys.csInquiry.aiAnalysisLatest,
    queryFn: () => csInquiryApi.getAiAnalysisLatest(),
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI 分析の履歴一覧を取得する。
 * @graph-connects csInquiryApi.getAiAnalysisHistory [calls] API クライアント
 */
export function useCsAiAnalysisHistory() {
  return useQuery({
    queryKey: queryKeys.csInquiry.aiAnalysisHistory,
    queryFn: () => csInquiryApi.getAiAnalysisHistory(),
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI 分析を実行し結果を保存するミューテーション。成功時に latest と history を再取得。
 * @graph-connects csInquiryApi.runAiAnalysis [calls] API クライアント
 */
export function useRunCsAiAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { from?: string; to?: string }) => csInquiryApi.runAiAnalysis(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.csInquiry.aiAnalysisLatest });
      void queryClient.invalidateQueries({ queryKey: queryKeys.csInquiry.aiAnalysisHistory });
    },
  });
}
